import {
  Avatar,
  Card,
  Col,
  Row,
  Space,
  Tag,
  Divider,
  Button,
  theme,
  Typography,
  Segmented,
  Alert,
} from "antd";
import { useContext, useMemo, useState } from "react";
import { EmployeeOperation } from "../../serviceoperation/EmployeeOperation";
import { AuthenticationContext } from "../../contexts/authentication-context";
import { CompanyContext } from "../../contexts/company-context";
import { PageContainer } from "@ant-design/pro-layout";
import ProfileDetail from "./profileDetail";
import { useParams } from "react-router";
import { EmployeeDetails } from "../../serviceoperation/entity/EmployeeOperationEntity";
import {
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  UserOutlined,
  LockOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

const ProfileLayout = () => {
  // Incoming URL Parameter.......
  const { id } = useParams();
  const empId = id ? String(id) : undefined;

  const userInfo = useContext(AuthenticationContext)?.userInfo;
  const { selectedCompany } = useContext(CompanyContext);
  const [userProfile, setUserProfile] = useState<EmployeeDetails>();
  const { token } = theme.useToken();
  const [currentTab, setCurrentTab] = useState<string>("profile");

  const handleTabChange = (value: string | number) => {
    setCurrentTab(String(value));
  };

  useMemo(() => {
    const employeeOps = new EmployeeOperation();
    const loggedInUserID = userInfo?.employeeCorporation
      ?.filter((y) => y.corporationId === selectedCompany?.corporationId)
      .map((y) => y.employeeId)
      .at(0);

    if (loggedInUserID || empId) {
      employeeOps
        .getEmployeeByIdAsync(
          selectedCompany?.corporationId as number,
          empId || loggedInUserID || "",
        )
        .then((x) => {
          setUserProfile(x);
        });
    }
  }, [selectedCompany, userInfo]);

  const getStatusColor = () => {
    if (userProfile?.invitePending && userProfile?.isActive)
      return "processing";
    if (userProfile?.isActive) return "success";
    return "error";
  };

  const getStatusLabel = () => {
    if (userProfile?.invitePending && userProfile?.isActive)
      return "Pending Invitation";
    if (userProfile?.isActive) return "Active";
    return "Terminated";
  };

  const renderSecuritySettings = () => {
    return (
      <Card
        style={{
          borderRadius: 12,
          backgroundColor: token.colorBgContainer,
          border: `1px solid ${token.colorBorder}`,
        }}
        bodyStyle={{ padding: 32 }}
      >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div>
            <h3 style={{ marginBottom: 16 }}>Password & Security</h3>
            <Alert
              message="Password Management via Azure AD"
              description="Your password is managed by Azure Active Directory (Azure AD). To change your password or update security settings, please use the Azure AD portal or contact your administrator."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
            <Button
              type="primary"
              icon={<LockOutlined />}
              onClick={() => {
                // Open Azure AD password change - this would typically be a deep link
                window.open(
                  "https://account.activedirectory.windowsazure.com/r#/profile",
                  "_blank",
                );
              }}
            >
              Manage Azure AD Account
            </Button>
          </div>
        </Space>
      </Card>
    );
  };

  const renderProfileInfo = () => {
    return (
      <div>
        {/* Row 1: Email & Phone */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12}>
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: token.colorBgLayout,
                borderRadius: 8,
              }}
            >
              <Space size={8} style={{ width: "100%" }}>
                <MailOutlined
                  style={{ color: token.colorPrimary, fontSize: 16 }}
                />
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "block" }}
                  >
                    Email
                  </Text>
                  <Text style={{ fontSize: 13, fontWeight: 500 }}>
                    {userProfile?.email || "Not set"}
                  </Text>
                </div>
              </Space>
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: token.colorBgLayout,
                borderRadius: 8,
              }}
            >
              <Space size={8} style={{ width: "100%" }}>
                <PhoneOutlined
                  style={{ color: token.colorPrimary, fontSize: 16 }}
                />
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "block" }}
                  >
                    Phone
                  </Text>
                  <Text style={{ fontSize: 13, fontWeight: 500 }}>
                    {userProfile?.phone || "Not set"}
                  </Text>
                </div>
              </Space>
            </div>
          </Col>
        </Row>

        {/* Row 2: Department & Organization */}
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12}>
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: token.colorBgLayout,
                borderRadius: 8,
              }}
            >
              <Space size={8} style={{ width: "100%" }}>
                <UserOutlined
                  style={{ color: token.colorPrimary, fontSize: 16 }}
                />
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "block" }}
                  >
                    Department
                  </Text>
                  <Text style={{ fontSize: 13, fontWeight: 500 }}>
                    {typeof userProfile?.department === "string"
                      ? userProfile.department
                      : userProfile?.department?.name || "Not set"}
                  </Text>
                </div>
              </Space>
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: token.colorBgLayout,
                borderRadius: 8,
              }}
            >
              <Space size={8} style={{ width: "100%" }}>
                <EnvironmentOutlined
                  style={{ color: token.colorPrimary, fontSize: 16 }}
                />
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "block" }}
                  >
                    Organization
                  </Text>
                  <Text style={{ fontSize: 13, fontWeight: 500 }}>
                    {userProfile?.organization?.branchName || "Not set"}
                  </Text>
                </div>
              </Space>
            </div>
          </Col>
        </Row>

        {/* Row 3: Employee ID & Manager */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: token.colorBgLayout,
                borderRadius: 8,
              }}
            >
              <Space size={8} style={{ width: "100%" }}>
                <UserOutlined
                  style={{ color: token.colorPrimary, fontSize: 16 }}
                />
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "block" }}
                  >
                    Employee ID
                  </Text>
                  <Text style={{ fontSize: 13, fontWeight: 500 }}>
                    {userProfile?.employeeId || "—"}
                  </Text>
                </div>
              </Space>
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div
              style={{
                padding: "12px 16px",
                backgroundColor: token.colorBgLayout,
                borderRadius: 8,
              }}
            >
              <Space size={8} style={{ width: "100%" }}>
                <UserOutlined
                  style={{ color: token.colorPrimary, fontSize: 16 }}
                />
                <div>
                  <Text
                    type="secondary"
                    style={{ fontSize: 11, display: "block" }}
                  >
                    Manager
                  </Text>
                  <Text style={{ fontSize: 13, fontWeight: 500 }}>
                    {userProfile?.managerName || "Not set"}
                  </Text>
                </div>
              </Space>
            </div>
          </Col>
        </Row>
      </div>
    );
  };

  const renderRightContent = () => {
    switch (currentTab) {
      case "profile":
        return renderProfileInfo();
      case "edit":
        return userProfile ? (
          <ProfileDetail empDetails={userProfile} />
        ) : (
          <div>Loading...</div>
        );
      case "security":
        return renderSecuritySettings();
      default:
        return renderProfileInfo();
    }
  };

  return (
    <PageContainer subTitle="Manage your profile information, preferences, and settings.">
      <Card
        style={{
          borderRadius: 12,
          backgroundColor: token.colorBgContainer,
          border: `1px solid ${token.colorBorder}`,
        }}
        bodyStyle={{ padding: 32 }}
      >
        <Row gutter={[32, 32]} align="top">
          {/* Left Sidebar - Fixed */}
          <Col xs={24} sm={24} md={7} style={{ textAlign: "center" }}>
            <Space
              direction="vertical"
              size="large"
              style={{ width: "100%", alignItems: "center" }}
            >
              <Avatar
                size={120}
                style={{ backgroundColor: token.colorPrimary }}
                icon={<UserOutlined />}
              />
              <div>
                <Title level={3} style={{ margin: 0 }}>
                  {userProfile?.firstName} {userProfile?.lastName}
                </Title>
                <Text type="secondary" style={{ fontSize: 14 }}>
                  {userProfile?.title || "Employee"}
                </Text>
              </div>
              <Tag
                color={getStatusColor()}
                style={{ fontSize: 12, padding: "6px 12px" }}
              >
                {getStatusLabel()}
              </Tag>
            </Space>
          </Col>

          <Divider type="vertical" style={{ height: "auto", minHeight: 200 }} />

          {/* Right Content - Changes based on tab */}
          <Col xs={24} sm={24} md={15}>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              {/* Tab Navigation */}
              <div style={{ textAlign: "left" }}>
                <Segmented
                  value={currentTab}
                  onChange={handleTabChange}
                  options={[
                    { label: "View Profile", value: "profile" },
                    { label: "Edit Profile", value: "edit" },
                    { label: "Security", value: "security" },
                  ]}
                  size="middle"
                />
              </div>

              {/* Dynamic Content */}
              {renderRightContent()}
            </Space>
          </Col>
        </Row>
      </Card>
    </PageContainer>
  );
};

// Add Title import
import Title from "antd/es/typography/Title";

export default ProfileLayout;
